import { isEqual } from 'lodash';
import * as moment from 'moment';
import * as pluralize from 'pluralize';
import { toast } from 'react-toastify';

import { AmexBalanceResetConfirmModal } from '@ReactComponents/Cards/CardsGatewayIssuingModal/Confirmation/AmexBalanceResetConfirmModal';
import { parseAndGetErrorMessage } from '@ReactComponents/Shared/ErrorAlert';

import { AppThunkDispatch, AppThunkGetState } from '@Reducers/index';

import { IGetIssuingDetailsResponse, IParentCardOption, CardIssuingApiService as cardIssuingApiService } from '@ClientApi/cardIssuing';
import { CARD_ISSUING_ERRORS, INSTITUTION_TYPE, NUMBER_OF_PHYSICAL_CARDS_ALLOWED_PER_USER } from '@Shared/constants';
import {
  AccountCapabilitiesCard,
  AccountCapabilitiesVirtualCard,
  CardType,
  ShippingOption,
  ShippingOptionServiceEnum
} from '@Shared/interfaces/CardsGatewayInterfaces';
import { CardIssuingBodyData } from '@Shared/interfaces/CardsIssuing';
import { ICardRequestModel, IUserModel } from '@Shared/models';

import { CloseModal, ShowModal } from './modalActions';
import { refreshUsersIfMissing } from './usersActions';

export const SET_LOADING = 'SET_ISSUING_MODAL_LOADING';
export const INITIALIZE_DATA = 'INITIALIZE_ISSUING_MODAL_DATA';
export const SET_SERVER_ERROR = 'SET_ISSUING_MODAL_SERVER_ERROR';
export const RESET = 'RESET_ISSUING_MODAL';

export interface ICardsGatewayIssuingModalValues {
  // Values
  id?: string;
  assigneeUserIds?: string[];
  assigneeUserId?: string;
  assigneeEmail?: string;
  parentCardId?: string;
  cardName?: string;
  allowanceId?: string;
  allowanceDailyLimit?: number;
  allowanceMerchantCategories: string[];
  allowanceMerchantGroupCategories: string[];
  allowanceVendor?: string;
  allowanceAmount?: number;
  allowanceInterval?: ICardRequestModel['frequency'];
  allowanceStartDate?: moment.Moment;
  allowanceEndDate?: moment.Moment;
  allowanceTransactionLimit?: number | null;
  cardType: CardType;
  addressRecipient?: string;
  addressStreet?: string;
  addressCity?: string;
  addressState?: string;
  addressZipCode?: string;
  addressPhoneNumber?: string;
  selectedCategoryId?: string | null;
  selectedExpenseTagsByBucketId: Record<string, string>;
  textExpenseTagsByBucketId?: Record<string, string>;
  cardRequestId?: string;
  cardRequestVersion?: number;
  firstName?: string;
  lastName?: string;
  shippingService?: ShippingOptionServiceEnum;
  shippingRequireSignature?: boolean;
  enableMultiSelect?: boolean;
  shippingOptions?: Record<'Standard' | 'Express' | 'Priority', ShippingOption[]>;
  cardTemplateId?: string;
}

export type LoadingState = boolean | 'body' | 'tags';

export class SetLoading {
  readonly type = SET_LOADING;

  constructor(public loading: LoadingState) {}
}

export class InitializeData {
  readonly type = INITIALIZE_DATA;

  constructor(public parentCardOptions?: IParentCardOption[], public issuingDetailsOriginalValues?: any) {}
}

export class SetServerError {
  readonly type = SET_SERVER_ERROR;

  constructor(public message: string) {}
}

export class Reset {
  readonly type = RESET;
}

const cardRequestAsIssuingDetails = (cardRequest: ICardRequestModel) =>
  ({
    id: cardRequest.id,
    user_id: cardRequest.user_id,
    description: cardRequest.purpose,
    default_category_id: cardRequest.default_category_id,
    expense_tags_to_linked_account: cardRequest.expense_tag_to_card_requests,
    allowance: {
      amount: cardRequest.budget,
      interval: cardRequest.frequency
    }
  } as unknown as IGetIssuingDetailsResponse);

export const init =
  (virtualMode: boolean, parentLinkedAccountId?: string, linkedAccountId?: string, cardRequest?: ICardRequestModel) =>
  async (dispatch: AppThunkDispatch, getState: AppThunkGetState) => {
    dispatch(new SetLoading('body'));

    let originalValuesPromise = null;
    if (cardRequest) {
      originalValuesPromise = Promise.resolve(cardRequestAsIssuingDetails(cardRequest));
    } else if (linkedAccountId) {
      originalValuesPromise = cardIssuingApiService.getIssuingDetails(linkedAccountId, false, true);
    }

    const parentCardOptionsPromise = virtualMode ? cardIssuingApiService.getParentCards(parentLinkedAccountId) : null;

    const [parentCardOptionsResponse, originalValues] = await Promise.all([parentCardOptionsPromise, originalValuesPromise]);

    // The issuing details only contains the user's email, so set the user_id too
    if (originalValues?.assigned_to) {
      const activeUsers = getState().users.usersActiveOrInvited;
      const matchingUser = activeUsers.find((user: IUserModel) => user.email === originalValues.assigned_to.email);
      originalValues.user_id = matchingUser?.id;
    }

    dispatch(new InitializeData(parentCardOptionsResponse?.results, originalValues));
  };

export const reset = () => new Reset();

export const submit =
  (
    parentLinkedAccountId: string,
    values: ICardsGatewayIssuingModalValues,
    linkedAccountId?: string,
    companyId?: string,
    issuingDetailsOriginalValues?: any,
    capabilities?: AccountCapabilitiesCard | AccountCapabilitiesVirtualCard,
    onSubmit?: (response?: any) => void
  ) =>
  async (dispatch: AppThunkDispatch) => {
    const isEdit = Boolean(linkedAccountId);

    if (isEdit) {
      if (issuingDetailsOriginalValues?.parent_card?.institution_type === INSTITUTION_TYPE.AMEX_CPG) {
        await makeAmexUpdateCardRequest(values, issuingDetailsOriginalValues, capabilities, linkedAccountId, companyId, dispatch, onSubmit);
      } else {
        await makeUpdateCardRequest(values, issuingDetailsOriginalValues, capabilities, linkedAccountId, companyId, dispatch, onSubmit);
      }
    } else {
      await makeCreateCardRequest(values, parentLinkedAccountId, capabilities, dispatch, onSubmit);
    }
  };

export type CardsGatewayIssuingModalAction = SetLoading | InitializeData | SetServerError | Reset;

async function makeAmexUpdateCardRequest(
  values: ICardsGatewayIssuingModalValues,
  issuingDetailsOriginalValues: any,
  capabilities: AccountCapabilitiesCard | AccountCapabilitiesVirtualCard,
  linkedAccountId: string,
  companyId: string,
  dispatch: AppThunkDispatch,
  onSubmit: (response?: any) => void
) {
  if (values.allowanceAmount !== issuingDetailsOriginalValues.allowance.amount) {
    dispatch(
      new ShowModal(AmexBalanceResetConfirmModal, {
        onConfirm: () =>
          makeUpdateCardRequest(values, issuingDetailsOriginalValues, capabilities, linkedAccountId, companyId, dispatch, onSubmit)
      })
    );
  } else {
    makeUpdateCardRequest(values, issuingDetailsOriginalValues, capabilities, linkedAccountId, companyId, dispatch, onSubmit);
  }
}

async function makeUpdateCardRequest(
  values: ICardsGatewayIssuingModalValues,
  issuingDetailsOriginalValues: any,
  capabilities: AccountCapabilitiesCard | AccountCapabilitiesVirtualCard,
  linkedAccountId: string,
  companyId: string,
  dispatch: AppThunkDispatch,
  onSubmit: (response?: any) => void
) {
  const body = buildUpdateRequestBody(values, issuingDetailsOriginalValues, capabilities);

  try {
    dispatch(new SetLoading('body'));
    dispatch(new SetServerError(null));
    const response = await cardIssuingApiService.updateIssuingDetails(linkedAccountId, body);
    dispatch(new Reset());
    dispatch(new CloseModal());
    onSubmit && onSubmit(response);
    dispatch(new SetLoading(false));
  } catch (error) {
    const errorMsg = parseAndGetErrorMessage(error.message) || 'Something went wrong. Please check form fields and try again.';
    dispatch(new SetLoading(false));
    dispatch(new SetServerError(errorMsg));
  }
}

export const resetCardsGatewayIssuingModal = () => new Reset();

async function makeCreateCardRequest(
  values: ICardsGatewayIssuingModalValues,
  parentLinkedAccountId: string,
  capabilities: AccountCapabilitiesCard | AccountCapabilitiesVirtualCard,
  dispatch: AppThunkDispatch,
  onSubmit: (response?: any) => void
) {
  const {
    parentCardId,
    assigneeUserIds,
    assigneeUserId,
    assigneeEmail,
    cardName,
    allowanceDailyLimit,
    allowanceMerchantCategories,
    allowanceMerchantGroupCategories,
    allowanceVendor,
    allowanceAmount,
    allowanceInterval,
    allowanceStartDate,
    allowanceEndDate,
    allowanceTransactionLimit,
    cardType,
    addressRecipient,
    addressStreet,
    addressCity,
    addressState,
    addressZipCode,
    addressPhoneNumber,
    selectedCategoryId,
    selectedExpenseTagsByBucketId,
    textExpenseTagsByBucketId,
    cardRequestId,
    cardRequestVersion,
    firstName,
    lastName,
    shippingService,
    shippingRequireSignature,
    cardTemplateId
  } = values;

  const expenseTagIds = Object.values(selectedExpenseTagsByBucketId);
  const multipleCards = assigneeUserIds?.length > 0;

  let body: CardIssuingBodyData = {
    assignee_user_ids: multipleCards ? assigneeUserIds : null,
    assignee_user_id: multipleCards ? null : assigneeUserId,
    assignee_email: multipleCards ? null : assigneeEmail,
    parent_linked_account_id: parentLinkedAccountId,
    card_name: cardName,
    is_ghost: cardType === CardType.Ghost,
    is_virtual: cardType === CardType.Virtual,
    allowance_daily_limit: allowanceDailyLimit,
    default_category_id: selectedCategoryId,
    expense_tag_ids: expenseTagIds,
    text_expense_tags: textExpenseTagsByBucketId,
    card_request_id: cardRequestId,
    card_request_version: cardRequestVersion,
    first_name: firstName,
    last_name: lastName,
    shipping_service: shippingService,
    shipping_require_signature: shippingRequireSignature,
    card_template_id: cardTemplateId
  };

  if (capabilities?.allowance?.transactionLimit) {
    body.allowance_transaction_limit = allowanceTransactionLimit;
  }

  if (cardType === CardType.Virtual) {
    body.parent_card_id = parentCardId;
  } else if (cardType === CardType.Physical) {
    body = {
      ...body,
      address_recipient: addressRecipient,
      address_street: addressStreet,
      address_city: addressCity,
      address_state: addressState,
      address_zip_code: addressZipCode,
      address_phone_number: addressPhoneNumber
    };
  }

  if (capabilities.allowance.startDate) {
    body.start_date = allowanceStartDate?.format('YYYY-MM-DD');
  }

  if (capabilities.allowance.endDate) {
    body.end_date = allowanceEndDate?.format('YYYY-MM-DD');
  }

  if (capabilities.allowance.intervalAmount) {
    body.allowance_amount = allowanceAmount;
    body.allowance_interval = allowanceInterval;
  }

  if (capabilities.allowance.supportedMerchantRestrictions > 0) {
    body.allowance_merchant_categories = allowanceMerchantCategories;
  }

  if (capabilities.allowance.supportedMerchantGroupRestrictions > 0) {
    body.allowance_merchant_group_categories = allowanceMerchantGroupCategories;
  }

  if (capabilities.allowance?.allowedVendor) {
    body.allowance_vendor = allowanceVendor;
  }

  try {
    dispatch(new SetLoading('body'));
    dispatch(new SetServerError(null));
    const response = await cardIssuingApiService.post(body);
    dispatch(new Reset());
    dispatch(new CloseModal());
    onSubmit && onSubmit(response);

    let containsInvite;
    if (Array.isArray(response)) {
      containsInvite = Boolean(response.find((res) => res.status && res.status === 'pending_invite'));
    } else {
      containsInvite = 'status' in response && response.status === 'pending_invite';
    }

    if (window.FEATURES.accelerated_card_issuing && containsInvite) {
      toast.success('Invite sent successfully.  Card will be issued once user accepts invite.', { autoClose: false });
      dispatch(refreshUsersIfMissing(response.user_id));
    } else {
      toast.success('Card Created!');
    }
  } catch (error) {
    const errorMessage = getMessageFromError(error);

    dispatch(new SetLoading(false));
    dispatch(new SetServerError(errorMessage));
  }
}

/**
 * This function will only update a subset of the form values. If adding new fields,
 * this list should be updated.
 */
function buildUpdateRequestBody(
  values: ICardsGatewayIssuingModalValues,
  issuingDetailsOriginalValues: any,
  capabilities: AccountCapabilitiesCard | AccountCapabilitiesVirtualCard
) {
  const {
    cardName,
    allowanceAmount,
    allowanceInterval,
    allowanceStartDate,
    allowanceEndDate,
    allowanceMerchantCategories,
    allowanceMerchantGroupCategories,
    allowanceVendor,
    allowanceTransactionLimit
  } = values;

  const body: Record<string, any> = {};

  if (window.FEATURES.emburse_cards_gateway_issuing_expense_fields) {
    const expenseTagIds = Object.values(values.selectedExpenseTagsByBucketId);

    // Something annoying is that undefined values will not be included in the request payload,
    // so need to send a different falsy value that is valid JSON.
    body.default_category_id = values.selectedCategoryId || false;
    body.expense_tag_ids = expenseTagIds;
    body.text_expense_tags = values.textExpenseTagsByBucketId;
  }

  const allowanceStartDateString = allowanceStartDate && allowanceStartDate.format('YYYY-MM-DD');
  const allowanceEndDateString = allowanceEndDate && allowanceEndDate.format('YYYY-MM-DD');

  const allowanceUpdated =
    allowanceAmount !== issuingDetailsOriginalValues.allowance.amount ||
    allowanceInterval !== issuingDetailsOriginalValues.allowance.interval ||
    allowanceStartDateString !== issuingDetailsOriginalValues.allowance.start_time ||
    allowanceEndDateString !== issuingDetailsOriginalValues.allowance.end_time ||
    allowanceTransactionLimit !== issuingDetailsOriginalValues.allowance.transaction_limit ||
    !isEqual(allowanceMerchantCategories, issuingDetailsOriginalValues.allowance.scope) ||
    !isEqual(allowanceMerchantGroupCategories, issuingDetailsOriginalValues.allowance.group_scope) ||
    !isEqual(allowanceVendor, issuingDetailsOriginalValues.allowance.vendor);

  if (cardName !== issuingDetailsOriginalValues.cardName) {
    body.card_name = cardName;
  }

  if (allowanceUpdated) {
    if (capabilities.allowance.startDate) {
      body.start_date = allowanceStartDateString;
    }

    if (capabilities.allowance.endDate) {
      body.end_date = allowanceEndDateString;
    }

    if (capabilities.allowance.intervalAmount) {
      body.allowance_amount = allowanceAmount;
      body.allowance_interval = allowanceInterval;
    }

    if (capabilities.allowance.supportedMerchantRestrictions > 0) {
      body.allowance_merchant_categories = allowanceMerchantCategories;
    }

    if (capabilities.allowance.supportedMerchantGroupRestrictions > 0) {
      body.allowance_merchant_group_categories = allowanceMerchantGroupCategories;
    }

    if (capabilities.allowance?.allowedVendor) {
      body.allowance_vendor = allowanceVendor;
    }

    if (capabilities.allowance.transactionLimit) {
      body.allowance_transaction_limit = allowanceTransactionLimit;
    }
  }
  return body;
}

function getMessageFromError(error: Error) {
  const message = error.message;

  if (message.includes(CARD_ISSUING_ERRORS.EXISTING_PHYSICAL_CARD)) {
    return `Users cannot be issued more than ${NUMBER_OF_PHYSICAL_CARDS_ALLOWED_PER_USER} physical ${pluralize(
      'card',
      NUMBER_OF_PHYSICAL_CARDS_ALLOWED_PER_USER
    )} from the same card program.`;
  } else if (message.includes(CARD_ISSUING_ERRORS.NO_PHONE_NUMBER)) {
    return "Employee doesn't have a phone number - card cannot be issued";
  } else if (message.includes(CARD_ISSUING_ERRORS.CARDHOLDER_STATUS_DEACTIVATED)) {
    return 'The card cannot be issued as this user’s profile doesn’t meet the American Express risk criteria and was declined. Please contact us if you need further assistance.';
  } else if (message.includes(CARD_ISSUING_ERRORS.CARDHOLDER_STATUS_PENDING)) {
    return 'The card cannot be issued as this user’s profile is undergoing compliance review. Please contact us if you need further assistance.';
  } else if (message.includes(CARD_ISSUING_ERRORS.INVALID_USER_ADDRESS)) {
    return 'You cannot issue a card to this user as it doesn’t have a US valid address';
  }

  return parseAndGetErrorMessage(error?.message) || 'Something went wrong. Please check form fields and try again.';
}
